import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { LocaleLink } from "@/components/LocaleLink";
import { Cookie, Settings2 } from "lucide-react";
import { useTranslation } from "react-i18next";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const STORAGE_KEY = "craftfindus_cookie_consent";

function getStoredPreferences(): CookiePreferences | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function storePreferences(prefs: CookiePreferences) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

const CookieBanner = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const stored = getStoredPreferences();
    if (!stored) {
      setVisible(true);
    }
  }, []);

  const accept = useCallback((prefs: CookiePreferences) => {
    storePreferences(prefs);
    setVisible(false);
  }, []);

  const acceptAll = () => {
    accept({ necessary: true, analytics: true, marketing: true });
  };

  const acceptSelected = () => {
    accept(preferences);
  };

  const rejectOptional = () => {
    accept({ necessary: true, analytics: false, marketing: false });
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[100] p-4 md:p-6"
      role="dialog"
      aria-label={t("cookies.title")}
    >
      <div className="mx-auto max-w-2xl rounded-sm border border-border bg-card shadow-xl">
        {/* Header */}
        <div className="flex items-start gap-3 p-5 pb-3">
          <Cookie className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
          <div className="flex-1">
            <h2 className="text-sm font-medium text-foreground">
              {t("cookies.title")}
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {t("cookies.description")}{" "}
              <LocaleLink to="/datenschutz" className="underline underline-offset-2 hover:text-foreground">
                {t("cookies.privacyLink")}
              </LocaleLink>
              .
            </p>
          </div>
        </div>

        {/* Config panel */}
        {showConfig && (
          <div className="border-t border-border px-5 py-4 space-y-3">
            <CookieRow
              label={t("cookies.necessary")}
              description={t("cookies.necessaryDesc")}
              checked
              disabled
            />
            <CookieRow
              label={t("cookies.analytics")}
              description={t("cookies.analyticsDesc")}
              checked={preferences.analytics}
              onChange={(v) => setPreferences((p) => ({ ...p, analytics: v }))}
            />
            <CookieRow
              label={t("cookies.marketing")}
              description={t("cookies.marketingDesc")}
              checked={preferences.marketing}
              onChange={(v) => setPreferences((p) => ({ ...p, marketing: v }))}
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2 border-t border-border px-5 py-4">
          <Button size="sm" onClick={acceptAll}>
            {t("cookies.acceptAll")}
          </Button>
          {showConfig ? (
            <Button size="sm" variant="outline" onClick={acceptSelected}>
              {t("cookies.saveSelection")}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowConfig(true)}
            >
              <Settings2 className="mr-1.5 h-3.5 w-3.5" />
              {t("cookies.configure")}
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={rejectOptional}>
            {t("cookies.onlyNecessary")}
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ---- small sub-component ---- */

function CookieRow({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-4 cursor-pointer">
      <div>
        <span className="text-sm font-medium text-foreground">{label}</span>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        aria-label={label}
      />
    </label>
  );
}

export default CookieBanner;
